// User types
export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  isAdmin: boolean;
  selectedProjectId?: string;
}

// Entity types (for admin impersonation)
export type EntityType = 'building' | 'service_provider';

export interface Building {
  id: string;
  name: string;
  address: string;
  yearBuilt?: string;
  buildingType?: string;
  floors: number;
  entrances: number;
  apartments: number;
  totalArea?: string;
  livingArea?: string;
  heating?: string;
  waterSupply?: string;
  sewerage?: string;
  electricity?: string;
  gas?: string;
  ventilation?: string;
  elevators?: string;
  foundation?: string;
  wallMaterial?: string;
  roofType?: string;
  windowType?: string;
  entranceDoors?: string;
  plotArea?: string;
  parking?: string;
  playground?: string;
  landscaping?: string;
  managementCompany?: string;
  managementPhone?: string;
  chairman?: string;
  chairmanPhone?: string;
  emergencyService?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceProvider {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  description?: string;
  logo?: string;
  website?: string;
  services: string[];
  address?: string;
  city?: string;
  // Estonian Business Registry data
  registryCode?: string;
  legalForm?: string;
  registrationDate?: string;
  capital?: number;
  vatNumber?: string;
  status?: string;
  economicActivities?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Service {
  id: string; // the category `key` (the unit used in orders & provider.services)
  name: string;
  icon: string;
  // Hierarchy + localization (optional; present once mapped from the API)
  key?: string;
  parentId?: string | null;
  nameEt?: string | null;
  nameEn?: string | null;
  nameRu?: string | null;
  isActive?: boolean;
}

export interface ServiceCategory {
  id: string;
  key: string;
  name: string;
  nameEt?: string | null;
  nameEn?: string | null;
  nameRu?: string | null;
  icon: string;
  sortOrder: number;
  parentId?: string | null;
  isActive?: boolean;
}

// A top-level node in the category tree: either a standalone leaf
// (isGroup=false, children=[]) or a grouping header with selectable leaf
// children (isGroup=true). Only leaves are orderable/selectable.
export interface CategoryNode {
  category: Service;
  children: Service[];
  isGroup: boolean;
}

// Auth response types
export interface BuildingAuthResponse {
  entityType: 'building';
  entityId: string;
  entityName: string;
  building: Building;
  // Set when an admin is impersonating this entity — preserves admin
  // capabilities (like access to docs) without forcing a re-auth.
  isAdmin?: boolean;
  // Whether this deployment has the assistant at all (ticket 21, §D15). The
  // frontend learns the flag from the existing /auth/me payload — one field,
  // no extra request.
  assistantEnabled?: boolean;
  // Whether this deployment has the translate buttons (ticket 23, §D10). Its
  // own flag, never a branch of the assistant's: false means the buttons are
  // not rendered at all, not rendered-and-disabled.
  translationEnabled?: boolean;
  // Whether this deployment reads uploaded documents (ticket 24). A fourth
  // independent flag on the same channel — it gates the widest disclosure of
  // the four, a whole document leaving for a processor, so it is nobody's
  // branch.
  recognitionEnabled?: boolean;
}

export interface ServiceProviderAuthResponse {
  entityType: 'service_provider';
  entityId: string;
  entityName: string;
  serviceProvider: ServiceProvider;
  // Set when an admin is impersonating this entity — preserves admin
  // capabilities (like access to docs) without forcing a re-auth.
  isAdmin?: boolean;
  assistantEnabled?: boolean;
  translationEnabled?: boolean;
  recognitionEnabled?: boolean;
}

export interface UserAuthResponse {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  isAdmin: boolean;
  selectedProjectId?: string;
  assistantEnabled?: boolean;
  translationEnabled?: boolean;
  recognitionEnabled?: boolean;
}

export type AuthResponse = BuildingAuthResponse | ServiceProviderAuthResponse | UserAuthResponse;

// Helper type guards
export function isBuildingAuth(auth: AuthResponse): auth is BuildingAuthResponse {
  return 'entityType' in auth && auth.entityType === 'building';
}

export function isServiceProviderAuth(auth: AuthResponse): auth is ServiceProviderAuthResponse {
  return 'entityType' in auth && auth.entityType === 'service_provider';
}

export function isUserAuth(auth: AuthResponse): auth is UserAuthResponse {
  return 'id' in auth && 'email' in auth && !('entityType' in auth);
}

// Project types
export interface Project {
  id: string;
  slug: string;
  name: string;
  description?: string;
  settings?: ProjectSettings;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectSettings {
  [key: string]: unknown;
}

// API Response types
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

// Socket events
export interface SocketEvents {
  connect: () => void;
  disconnect: () => void;
  error: (data: { message: string }) => void;
  authRequired: () => void;
  projects: (projects: Project[]) => void;
}

// News types
export interface NewsSource {
  id: string;
  name: string;
  url: string;
  isActive: boolean;
  lastParsed?: string;
  _count?: {
    articles: number;
  };
}

export interface NewsArticle {
  id: string;
  title: string;
  summary?: string;
  content?: string;
  url: string;
  imageUrl?: string;
  publishedAt?: string;
  isRelevant: boolean;
  relevanceScore?: number;
  keywords: string[];
  category?: string;
  sourceId: string;
  source: {
    id: string;
    name: string;
    url: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface NewsPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

// Service Order types
export type ServiceOrderStatus = 'OPEN' | 'IN_REVIEW' | 'CONTRACTED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ARCHIVED';
export type ProposalStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'WITHDRAWN';

export interface ServiceOrder {
  id: string;
  serviceType: string;
  serviceName: string;
  title: string;
  description: string;
  status: ServiceOrderStatus;
  buildingId: string;
  building?: {
    id: string;
    name: string;
    address: string;
    city?: string;
  };
  contractedProviderId?: string;
  contractedProvider?: {
    id: string;
    name: string;
    phone?: string;
    email?: string;
  };
  proposals?: ServiceProposal[];
  hasApplied?: boolean;
  myProposal?: { id: string; status: ProposalStatus } | null;
  isFavorited?: boolean;
  // Report status (from list API)
  reportStatus?: 'DRAFT' | 'SUBMITTED' | 'REJECTED' | 'ACCEPTED' | 'SIGNED' | null;
  // Invoice fields
  invoiceFileKey?: string;
  invoiceFileName?: string;
  invoiceNumber?: string;
  invoiceAmount?: number;
  invoiceUploadedAt?: string;
  paymentConfirmedAt?: string;
  paymentConfirmedBy?: string;

  attachments?: ReportAttachment[];
  serviceCategoryId?: string;
  serviceCategory?: ServiceCategory;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceProposal {
  id: string;
  message?: string;
  price?: number;
  timeline?: string;
  status: ProposalStatus;
  orderId: string;
  order?: ServiceOrder & {
    // Building info is only available when proposal is ACCEPTED
    building?: {
      id: string;
      name: string;
      address: string;
      managementPhone?: string;
    };
  };
  providerId: string;
  provider?: {
    id: string;
    name: string;
    services: string[];
    serviceNames?: string[];
  };
  createdAt: string;
  updatedAt: string;
}

// Contract types
export interface WorkItem {
  description: string;
  unit?: string;
  quantity?: number;
  price?: number;
  total?: number;
}

export type ContractStatus = 'DRAFT' | 'PROVIDER_SIGNED' | 'BUILDING_SIGNED' | 'FULLY_SIGNED' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';

export interface Contract {
  id: string;
  orderId: string | null;
  buildingId: string;
  providerId: string;
  services: string[];
  title: string;
  description?: string;
  price?: number;
  timeline?: string;
  terms?: string;
  startDate?: string;
  endDate?: string;
  workItems?: WorkItem[] | null;
  status: ContractStatus;
  isUploaded?: boolean;
  prepaymentPercent?: number;
  paymentDays?: number;
  cancelReason?: string;
  cancelledBy?: string;
  providerFileKey?: string;
  providerFileName?: string;
  providerSignedAt?: string;
  buildingFileKey?: string;
  buildingFileName?: string;
  buildingSignedAt?: string;
  finalFileKey?: string;
  finalFileName?: string;
  building?: { id: string; name: string };
  provider?: { id: string; name: string; services?: string[]; phone?: string | null };
  order?: ServiceOrder;
  createdAt: string;
  updatedAt: string;
}

// ==================== Partner assignments (ticket 2) ====================

/** Server-fetched company identity stored on offline partner assignments. */
export interface RegistryActivity {
  name: string;
  code?: string;
}

export interface RegistrySnapshot {
  name: string;
  registryCode: string;
  address?: string;
  phone?: string;
  legalForm?: string;
  /**
   * EMTAK activities the company declared. Present only on registry previews —
   * never stored on an assignment, so an offline partner card has none.
   */
  activities?: RegistryActivity[];
  /**
   * Provenance when the pick came from the registry mirror (ticket 4): carries
   * the data date so the licence attribution can follow the data. Client-side
   * only — the server never persists it.
   */
  localRegistry?: LocalRegistryInfo;
}

// ---- Registry mirror (ticket 4) ----

export type RegistryStatusCode = 'R' | 'L' | 'N' | 'unknown';
export type RegistryMatchTier = 'main' | 'declared' | 'related';

export interface RegistryMirrorActivity {
  code: string;
  version: number;
  name: string;
  primary: boolean;
}

export interface RegistryCategoryMatch {
  key: string;
  tier: RegistryMatchTier;
  evidence: { code: string; name: string };
}

/** What the local copy of äriregister knows about a company (preview card block). */
export interface LocalRegistryInfo {
  dataDate: string | null;
  status: RegistryStatusCode;
  statusText: string | null;
  legalForm: string | null;
  activities: RegistryMirrorActivity[];
  categoryMatch: RegistryCategoryMatch[];
}

/** One row of GET /api/service-providers/registry/search. Live rows carry nulls. */
export interface RegistrySearchRow {
  name: string;
  registryCode: string;
  legalForm: string | null;
  status: RegistryStatusCode | null;
  municipality: string | null;
  hasEmail: boolean | null;
  activities: RegistryMirrorActivity[];
  categoryMatch: RegistryCategoryMatch[];
}

export interface RegistrySearchMeta {
  source: 'mirror' | 'live';
  dataDate: string | null;
  stale: boolean;
  truncated: boolean;
  mappedCategories: string[];
  reason?: 'no_mirror' | 'unmapped_category';
}

/** Summary DTO of GET /api/partner-assignments — no document arrays. */
/**
 * What the registry copy says about a stored partner *now* (BUG-36). The
 * snapshot beside it is the company as it was when the building recorded it,
 * and a company can enter liquidation afterwards; `dataDate`/`stale` say how
 * old the copy that claims this is. Absent when the mirror has no row for the
 * code, or could not be asked.
 */
export interface RegistryStanding {
  status: RegistryStatusCode;
  statusText: string | null;
  dataDate: string | null;
  stale: boolean;
}

export interface PartnerAssignment {
  id: string;
  categoryKey: string;
  providerId: string | null;
  provider?: { id: string; name: string } | null;
  registrySnapshot: RegistrySnapshot | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  /** Ticket 3.7: REGISTRY when the e-mail equalled the registry profile at save time, MANUAL when typed. */
  contactEmailSource?: 'REGISTRY' | 'MANUAL' | null;
  /**
   * BUG-39: the company this contact was inherited from when the partner was
   * replaced and the contact was kept. `null` once anyone edits the contact.
   */
  contactInheritedFrom?: string | null;
  assignedAt: string;
  version: number;
  updatedAt: string;
  registryStanding?: RegistryStanding;
}

export interface PartnerAssignmentDocument {
  id: string;
  fileName: string;
  size: number;
  mimeType: string;
  /** UPLOADED by the building, or an unsigned draft GENERATED by KYT (ticket 3.7). */
  origin?: 'UPLOADED' | 'GENERATED';
  uploadedByName: string | null;
  uploadedAt: string;
}

// ==================== Add partner (ticket 3.7) ====================

export type AccountState = 'contract_allowed' | 'invitation_required' | 'invitation_pending' | 'blocked';

/** GET /api/partner-assignments/registry-profile/:code — the add-partner pick preview. */
export interface RegistryProfile {
  verdict: 'ok' | 'not_active' | 'not_found' | 'unavailable';
  snapshot: RegistrySnapshot | null;
  email: string | null;
  localRegistry?: LocalRegistryInfo;
  /** A KYT account claiming the code — disclosure only, never a gate. */
  account: { providerId: string; providerName: string; state: AccountState } | null;
}

export interface AssignResult {
  outcome: 'offline_assigned' | 'no_change';
  categories: Array<{ categoryKey: string; result: 'assigned' | 'contact_updated' | 'no_change'; assignmentId: string }>;
  documentIds: string[];
  account: RegistryProfile['account'];
}

/**
 * Two-source standing resolution for a category: the winning eligible framework
 * contract, or an offline assignment that overlays it (newest fact wins).
 */
export type StandingResolution =
  | { kind: 'contract'; contract: Contract; collisionCount: number }
  | { kind: 'offline'; assignment: PartnerAssignment; shadowedContract?: Contract };

export type PartnerSaveOutcome =
  | 'offline_assigned'
  | 'contract_required'
  | 'invitation_required'
  | 'invitation_pending'
  | 'blocked'
  | 'contact_updated'
  | 'no_change';

export type AddendumStatus = 'DRAFT' | 'PROVIDER_SIGNED' | 'BUILDING_SIGNED' | 'FULLY_SIGNED' | 'CANCELLED';

export interface ContractAddendum {
  id: string;
  contractId: string;
  orderId: string | null;
  title: string;
  description?: string;
  workItems?: WorkItem[] | null;
  totalAmount?: number;
  initiator: 'building' | 'service_provider';
  status: AddendumStatus;
  isUploaded: boolean;
  providerFileKey?: string;
  providerFileName?: string;
  providerSignedAt?: string;
  buildingFileKey?: string;
  buildingFileName?: string;
  buildingSignedAt?: string;
  finalFileKey?: string;
  finalFileName?: string;
  cancelReason?: string;
  cancelledBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ContractHistory {
  id: string;
  contractId: string;
  actorType: string;
  actorId: string;
  actorName?: string;
  action: string;
  details?: Record<string, unknown>;
  oldFileKey?: string;
  newFileKey?: string;
  createdAt: string;
}

// Work Acceptance Report types
export type ReportStatus = 'DRAFT' | 'SUBMITTED' | 'REJECTED' | 'ACCEPTED' | 'SIGNED';

export type ReportAttachment = {
  key: string;
  fileName: string;
  mimeType: string;
  size: number;
};

export type WorkAcceptanceReport = {
  id: string;
  orderId: string;
  buildingId: string;
  providerId: string;
  reportNumber: string;
  status: ReportStatus;
  workPeriodStart?: string;
  workPeriodEnd?: string;
  totalAmount?: number;
  workItems?: WorkItem[];
  attachments?: ReportAttachment[];
  isUploaded: boolean;
  rejectionReason?: string;
  fileKey?: string;
  fileName?: string;
  signedFileKey?: string;
  signedFileName?: string;
  signedAt?: string;
  building?: {
    id: string;
    name: string;
    address: string;
  };
  provider?: {
    id: string;
    name: string;
    registryCode?: string;
  };
  createdAt: string;
  updatedAt: string;
};

export type WorkAcceptanceReportHistory = {
  id: string;
  reportId: string;
  actorType: string;
  actorId: string;
  actorName: string;
  action: string;
  details?: Record<string, unknown>;
  createdAt: string;
};

